import { 
  Home as HomeIcon, 
  AdminPanelSettings as AdminIcon,
  Work as WorkIcon,
  Description as ResumeIcon,
  RecordVoiceOver as InterviewIcon,
  SmartToy as AgentIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';

/**
 * Role-based access control configuration
 * Defines which roles have access to which navigation items and routes
 */

export type UserRole = 'admin' | 'hr' | 'sme' | 'panelist' | 'user' | 'super_admin'; // Changed interviewer to panelist

export interface NavigationItem {
  label: string;
  path: string;
  icon: any; // Material-UI icon component
  public: boolean;
  allowedRoles: UserRole[];
  description?: string;
}

/**
 * Role hierarchy and access rules:
 * - Admin: Access to all tabs
 * - HR: Access to all tabs except Admin Panel
 * - SME: Access to Home, Jobs (for review), Resumes (assigned), Agent, My Account
 * - Interviewer: Access to Home, Interview Panel, Agent & My Account
 * - User: Access to Home, Agent & My Account
 */
export const ROLE_ACCESS_MAP: Record<UserRole, UserRole[]> = {
  admin: ['admin', 'hr', 'sme', 'panelist', 'user'],
  hr: ['hr', 'sme', 'panelist', 'user'],
  sme: ['sme'],
  panelist: ['panelist'], // Changed interviewer to panelist
  user: ['user'],
  super_admin: ['admin', 'hr', 'sme', 'panelist', 'user'], // Super Admin has all permissions
};

/**
 * Check if a user role has access to a specific feature/route
 */
export const hasRoleAccess = (userRole: UserRole | undefined, allowedRoles: UserRole[]): boolean => {
  if (!userRole) return false;
  
  // Admin has access to everything
  if (userRole === 'admin') return true;
  
  // Check if user's role is in the allowed roles
  return allowedRoles.includes(userRole);
};

/**
 * Get all roles that have access to a specific set of allowed roles
 */
export const getRolesWithAccess = (allowedRoles: UserRole[]): UserRole[] => {
  const rolesWithAccess: UserRole[] = [];
  
  Object.entries(ROLE_ACCESS_MAP).forEach(([role, permissions]) => {
    if (allowedRoles.some(allowedRole => permissions.includes(allowedRole))) {
      rolesWithAccess.push(role as UserRole);
    }
  });
  
  return rolesWithAccess;
};

/**
 * Navigation items configuration with role-based access
 */
export const getNavigationConfig = (): NavigationItem[] => [
  {
    label: 'Home',
    path: '/',
    icon: HomeIcon,
    public: true,
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    description: 'Home page - accessible to all users',
  },
  {
    label: 'Admin',
    path: '/admin',
    icon: AdminIcon,
    public: false,
    allowedRoles: ['admin'],
    description: 'Admin panel - admin only',
  },
  {
    label: 'Positions',
    path: '/hr-dashboard',
    icon: WorkIcon,
    public: false,
    allowedRoles: ['admin', 'hr', 'sme'],
    description: 'Job positions and requisitions management - admin, HR, and SME',
  },
  {
    label: 'Candidates',
    path: '/sme-dashboard',
    icon: ResumeIcon,
    public: false,
    allowedRoles: ['admin', 'hr', 'sme'],
    description: 'Candidate screening and resume reviews - admin, HR, and SME',
  },
  {
    label: 'Interviews',
    path: '/interview-dashboard',
    icon: InterviewIcon,
    public: false,
    allowedRoles: ['admin', 'hr', 'panelist'],
    description: 'Interview scheduling and management - admin, HR, and panelist',
  },
  {
    label: 'AI Assistant',
    path: '/agent',
    icon: AgentIcon,
    public: false,
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    description: 'AI-powered recruitment assistant - accessible to all authenticated users',
  },
  {
    label: 'Analytics',
    path: '/excel-comparison',
    icon: ReportsIcon,
    public: false,
    allowedRoles: ['admin', 'hr'],
    description: 'Recruitment analytics and insights - admin and HR only',
  },
];

/**
 * Route protection configuration
 */
export interface RouteConfig {
  path: string;
  allowedRoles: UserRole[];
  requiresAuth: boolean;
  fallbackPath?: string;
}

export const getRouteConfig = (): RouteConfig[] => [
  {
    path: '/',
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    requiresAuth: false,
  },
  {
    path: '/login',
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    requiresAuth: false,
  },
  {
    path: '/admin',
    allowedRoles: ['admin'],
    requiresAuth: true,
    fallbackPath: '/',
  },
  {
    path: '/hr-dashboard',
    allowedRoles: ['admin', 'hr', 'sme'],
    requiresAuth: true,
    fallbackPath: '/',
  },
  {
    path: '/sme-dashboard',
    allowedRoles: ['admin', 'hr', 'sme'],
    requiresAuth: true,
    fallbackPath: '/',
  },
  {
    path: '/interview-dashboard',
    allowedRoles: ['admin', 'hr', 'panelist'], // Changed interviewer to panelist
    requiresAuth: true,
    fallbackPath: '/',
  },
  {
    path: '/agent',
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    requiresAuth: true,
    fallbackPath: '/login',
  },
  {
    path: '/agent/:candidateId',
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    requiresAuth: true,
    fallbackPath: '/login',
  },
  {
    path: '/account',
    allowedRoles: ['admin', 'hr', 'sme', 'panelist', 'user'],
    requiresAuth: true,
    fallbackPath: '/login',
  },
  {
    path: '/excel-comparison',
    allowedRoles: ['admin', 'hr'],
    requiresAuth: true,
    fallbackPath: '/',
  },
  {
    path: '/admin-dashboard',
    allowedRoles: ['admin', 'super_admin'],
    requiresAuth: true,
    fallbackPath: '/',
  },
];